import { OneWayInServiceUpsertDto } from 'src/api/v2';
import useServiceSubForm, { extractSubData, SubFormProps, SubFormReturn } from '../use-service-sub-form';
import { formatDateInputDate } from 'src/utils/FormatValue';
import { useTranslation } from 'react-i18next';
import { MutableRefObject } from 'react';
import { PURPOSE_MAX_LENGTH } from '../../constants/service-form-max-lengths';
import { isValidityRangeReversed } from 'src/features/services/utils/service-utils';

export type AboutFields =
  | 'name'
  | 'validFrom'
  | 'validTo'
  | 'description'
  | 'customerDescription'
  | 'servicePurpose'
  | 'keywords'
  | 'externalLinks';
export type AboutData = Pick<OneWayInServiceUpsertDto, AboutFields>;

type UseAboutFormProps = SubFormProps<AboutData> & {
  purposeRawTextRef?: MutableRefObject<string>;
};

const useAboutForm = ({
  data,
  updateData,
  errors,
  setErrors,
  purposeRawTextRef,
}: UseAboutFormProps): SubFormReturn<AboutData, AboutFields> => {
  const { t } = useTranslation('services');
  const rules: Partial<Record<AboutFields, any>> = {
    name: {
      required: t('about.serviceName.required'),
      maxLength: { value: 500, message: t('about.serviceName.maxLength') },
    },
    validFrom: {
      required: t('about.validFrom.required'),
      setValueAs: (value) => (value === '' ? undefined : value),
      onBlur: () => {
        if (form.getValues('validTo')) form.trigger('validTo');
      },
    },
    validTo: {
      required: t('about.validTo.required'),
      setValueAs: (value) => (value === '' ? undefined : value),
      validate: (value) =>
        isValidityRangeReversed(form.getValues('validFrom'), value)
          ? t('about.validTo.beforeValidFrom')
          : true,
    },
    servicePurpose: {
      validate: (value) => {
        if (!value || value === '' || value === '<p><br></p>') {
          return t('about.purpose.required');
        }
        const textLength = purposeRawTextRef?.current
          ? purposeRawTextRef.current.length
          : (() => {
              const div = document.createElement('div');
              div.innerHTML = value;
              return (div.textContent || '').length;
            })();
        if (textLength > PURPOSE_MAX_LENGTH) {
          return t('about.purpose.maxLength');
        }
        return true;
      },
    },
    description: {
      validate: (value) => {
        if (!value || value === '' || value === '<p><br></p>') {
          return t('about.description.required');
        }
        return true;
      },
    },
    keywords: {
      validate: (value) => {
        if (!value || !Array.isArray(value) || value.length === 0) {
          return t('about.keywords.required');
        }
        return true;
      },
    },
  };

  const extractAboutFields = (data: OneWayInServiceUpsertDto): AboutData => {
    return extractSubData(data, [
      'name',
      'validFrom',
      'validTo',
      'description',
      'customerDescription',
      'servicePurpose',
      'keywords',
      'externalLinks',
    ]);
  };

  const aboutFields = extractAboutFields(data);
  const formattedFields = {
    ...aboutFields,
    validFrom: aboutFields.validFrom ? (formatDateInputDate(aboutFields.validFrom) as any) : undefined,
    validTo: aboutFields.validTo ? (formatDateInputDate(aboutFields.validTo) as any) : undefined,
  };
  const { form } = useServiceSubForm<AboutData>(formattedFields, updateData, errors, setErrors);
  const registerWithRules = (field: AboutFields) => form.register(field, rules[field]);
  return { form, rules, registerWithRules };
};

export default useAboutForm;
